import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetMarketSearchListMutation } from "../../../../api/services/manageUser/manageUserApi";
import {
  ConsolidatedReport,
  MarketSearchResponse,
  SearchRegionResponse,
} from "../../../../api/services/manageUser/models";
import { useGetSearchRegionMutation } from "../../../../api/services/mdmOffice/mdmOfficeApis";
import { useGetPowerBiReports } from "../../../../api/services/reports/reportsApi";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiGrid,
  MuiMultiSelect,
  MuiOptionMenu,
  MuiTable,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
  RowProps,
} from "../../../../components";
import { MAX_LENGTH_1000 } from "../../../../helpers/constants";
import { isNotEmpty } from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { storeAddedReportsFromEditData } from "../../../../redux/slices/manageUser/manageUserSlice";
import { PowerBiReportValues } from "../../../reports/modelsAndSchemas/models";
import {
  AddedReport,
  MarketRegions,
  PowerBiReportsDetailKey,
  ViewUserDataType,
} from "../../modelsAndSchemas/models";
import { createUserSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateUserPowerBiReportsDetailWrapper } from "./CreateUserPowerBiReportsDetail.style";

interface CreateUserPowerBiReportsDetailProps {
  pageId: string;
  isEditUser: boolean;
  editUserData?: ViewUserDataType;
  addedReports: AddedReport[];
  setAddedReports: React.Dispatch<React.SetStateAction<AddedReport[]>>;
  handlePowerBIEditIndexChange: (index: number | null) => void;
}

export const CreateUserPowerBiReportsDetail: FC<
  CreateUserPowerBiReportsDetailProps
> = ({
  pageId,
  isEditUser,
  editUserData,
  addedReports,
  setAddedReports,
  handlePowerBIEditIndexChange,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { commonLabels, fetchLabel, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const { control, getValues, setValue, reset, trigger } = useFormContext();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const reportsRef = useRef<{ resetValues: (values?: string[]) => void }>(null);

  const [reportsList, setReportsList] = useState<OptionsDataType[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [reportNameEdit, setReportNameEdit] = useState<string>("");

  const getPowerBiReportsDetailLabel = (key: string) =>
    fetchLabel(createUserSiteCoreKeys.powerBIReportsKey, key);

  //Repetitive keys kept in an object for reusability
  const powerBiSiteCoreLabel = {
    reportColumnHeader: getPowerBiReportsDetailLabel(
      createUserSiteCoreKeys.reportColumnHeader,
    ),

    actionColumnHeader: getPowerBiReportsDetailLabel(
      createUserSiteCoreKeys.actionColumnHeader,
    ),
    reports: getPowerBiReportsDetailLabel(createUserSiteCoreKeys.reports),
    all: getPowerBiReportsDetailLabel(createUserSiteCoreKeys.all),
  };

  const geoTypeOptions = fetchDropdownValues(
    createUserSiteCoreKeys.powerBIReportsKey,
    createUserSiteCoreKeys.geoTypeList,
    true,
  );

  const {
    data: reportListData,
    isLoading: isLoadingReportsData,
    error: isErrorReportsData,
  } = useGetPowerBiReports(false);

  const {
    mutate: getSearchRegion,
    data: searchRegionData,
    isPending: isPendingSearchRegion,
    isError: isErrorSearchRegion,
  } = useGetSearchRegionMutation();

  const {
    mutate: getMarketSearchList,
    data: marketListData,
    isPending: isPendingMarketSearchList,
    isError: isErrorMarketSearchList,
  } = useGetMarketSearchListMutation();

  const geoTypeOptionsSiteCore = fetchDropdownValues(
    createUserSiteCoreKeys.powerBIReportsKey,
    createUserSiteCoreKeys.geoTypeList,
  );

  useEffect(() => {
    getSearchRegion("");
    getMarketSearchList([]);
  }, []);

  useEffect(() => {
    handlePowerBIEditIndexChange(editingIndex);
  }, [editingIndex, handlePowerBIEditIndexChange]);

  // transform reports API data
  useEffect(() => {
    if (isNotEmpty(reportListData)) {
      const transformedReportList = reportListData.map(
        (report: PowerBiReportValues) => ({
          label: report.name,
          value: report.id,
        }),
      );
      setReportsList(transformedReportList);
    }
  }, [reportListData]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isLoadingReportsData ||
          isPendingSearchRegion ||
          isPendingMarketSearchList,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoadingReportsData, isPendingSearchRegion, isPendingMarketSearchList]);

  useEffect(() => {
    if (isErrorReportsData || isErrorSearchRegion || isErrorMarketSearchList) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isErrorReportsData, isErrorSearchRegion, isErrorMarketSearchList]);

  const resetFormFields = () => {
    const defaultGeoType = geoTypeOptions?.[0]?.value;
    const currentComments = getValues(PowerBiReportsDetailKey.comments);
    reset({
      reports: [],
      geoType: defaultGeoType,
      salesRegion: [],
      salesMarket: [],
      comments: currentComments ?? "",
    });

    reportsRef.current?.resetValues([]);
  };

  // This function is called by edit action from the table menu but action item "edit" was removed based on the requirements, hence function is unused.
  const onEditTableRow = (index: number) => {
    setEditingIndex(index);
    const row = addedReports[index];
    const selectedReportId = row.reportId;
    // find matching option from availableReports
    setTimeout(() => {
      const reportValue = reportsList.find(
        (r) => r.value === selectedReportId,
      )?.value;
      setValue(
        PowerBiReportsDetailKey.reports,
        reportValue ? [reportValue] : [],
      );

      if (isNotEmpty([reportValue])) {
        const selectedReport = reportsList.find((x) => x.value === reportValue);
        setReportNameEdit(selectedReport?.label || "");
      }
    });
  };

  const onDeleteTableRow = (index: number) => {
    setAddedReports((prev) => prev.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      resetFormFields();
    }
  };

  const defaultGeoType = geoTypeOptionsSiteCore[0]?.value;
  useEffect(() => {
    if (
      isEditUser &&
      editUserData?.reports?.powerBIReport &&
      isNotEmpty(reportsList) &&
      isNotEmpty(geoTypeOptionsSiteCore)
    ) {
      const transformedReports = editUserData.reports.powerBIReport.map(
        (r) => ({
          reportId: r.reportId,
          reportName: reportsList.find((x) => x.value === r.reportId)?.label,
          geoType: defaultGeoType,
          // Only unique region IDs
          salesRegion: Array.from(
            new Set(
              r.regionMarket.map((rm: MarketRegions) => rm.salesRegionId),
            ),
          ),
          salesMarket: r.regionMarket.map((rm: MarketRegions) => rm.id),
        }),
      );
      setAddedReports(transformedReports);
      dispatch(
        storeAddedReportsFromEditData(
          transformedReports as ConsolidatedReport[],
        ),
      );
      if (editUserData?.reports?.comment) {
        setValue(
          PowerBiReportsDetailKey.comments,
          editUserData.reports.comment,
        );
      }
    }
  }, [isEditUser, editUserData, setAddedReports, reportsList, defaultGeoType]);

  const handleAddReports = async () => {
    const isValid = await trigger();

    if (!isValid) {
      return;
    }
    const values = getValues();
    const selectedReports = values.reports || [];

    if (selectedReports.length === 0) return;

    const newReports = selectedReports.map((id: string) => {
      const report = reportsList.find((r) => r.value === id);
      const allMarketIds =
        marketListData?.map((market: MarketSearchResponse) => market.id) || [];

      const allRegionIds = isNotEmpty(searchRegionData)
        ? searchRegionData.map((region: SearchRegionResponse) => region.id)
        : [];

      return {
        reportId: id,
        reportName: report?.label,
        geoType: values.geoType,
        salesRegion: allRegionIds,
        salesMarket: allMarketIds,
      };
    });

    if (editingIndex !== null) {
      // update existing row
      setAddedReports((prev) => {
        const updated = [...prev];
        updated[editingIndex] = newReports[0];
        return updated;
      });
      setEditingIndex(null);
    } else {
      // add new rows (unique by reportId)
      setAddedReports((prev) =>
        [...prev, ...newReports].filter(
          (v, i, arr) => arr.findIndex((x) => x.reportId === v.reportId) === i,
        ),
      );
    }
    resetFormFields();
  };

  // filter reports dropdown to exclude already-added reports
  const availableReports = useMemo(() => {
    return reportsList.filter((report) => {
      if (editingIndex !== null) {
        const editingReportId = addedReports[editingIndex]?.reportId;
        return (
          report.value === editingReportId ||
          !addedReports.some((r) => r.reportId === report.value)
        );
      }
      return !addedReports.some((r) => r.reportId === report.value);
    });
  }, [reportsList, addedReports, editingIndex]);

  const columns = [
    {
      id: "reportName",
      label: powerBiSiteCoreLabel.reportColumnHeader,
      isSortVisible: true,
      colSpan: 2,
      render: (row: RowProps) => (
        <MuiBox>
          <MuiTypography variant="h3">{row.reportName}</MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: powerBiSiteCoreLabel.actionColumnHeader,
      label: powerBiSiteCoreLabel.actionColumnHeader,
      render: (row: RowProps) => {
        const index = addedReports.findIndex(
          (r) => r.reportId === row.reportId,
        );
        const actions = [
          {
            label: commonLabels.delete,
            dataTestId: `${pageId}-delete`,
            onClick: () => onDeleteTableRow(index),
          },
        ];
        return <MuiOptionMenu menuItems={actions} />;
      },
    },
  ];

  const onCancelPowerBiReportsForm = () => {
    resetFormFields();
    setEditingIndex(null);
  };

  return (
    <CreateUserPowerBiReportsDetailWrapper>
      <MuiGrid container className="user-role-email-report-grid">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={PowerBiReportsDetailKey.userEmail}
          >
            <MuiTextField
              id={`${pageId}-${PowerBiReportsDetailKey.userEmail}`}
              label={getPowerBiReportsDetailLabel(
                createUserSiteCoreKeys.userEmailReport,
              )}
              disabled
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={PowerBiReportsDetailKey.firstName}
          >
            <MuiTextField
              id={`${pageId}-${PowerBiReportsDetailKey.firstName}`}
              label={getPowerBiReportsDetailLabel(
                createUserSiteCoreKeys.firstNameReport,
              )}
              disabled
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={PowerBiReportsDetailKey.lastName}
          >
            <MuiTextField
              id={`${pageId}-${PowerBiReportsDetailKey.lastName}`}
              label={getPowerBiReportsDetailLabel(
                createUserSiteCoreKeys.lastNameReport,
              )}
              showCloseIcon
              disabled
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>

      <MuiBox>
        <AccordionHeader
          className="accordion-header"
          title={
            <AccordionHeaderText
              label={getPowerBiReportsDetailLabel(
                createUserSiteCoreKeys.reportAccessSection,
              )}
            />
          }
          showExpandCollapseIcon={false}
          isExpanded={true}
        >
          <MuiBox className="accordion-container">
            <MuiTypography
              variant="h3SemiBold"
              className="role-header-text"
              component="div"
            >
              {editingIndex !== null
                ? getPowerBiReportsDetailLabel(
                    createUserSiteCoreKeys.editReport,
                  )
                : getPowerBiReportsDetailLabel(
                    createUserSiteCoreKeys.addReport,
                  )}
            </MuiTypography>

            <MuiGrid container>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                {editingIndex !== null ? (
                  <MuiTextField
                    id={`${pageId}-${PowerBiReportsDetailKey.reports}`}
                    label={powerBiSiteCoreLabel.reports}
                    disabled
                    value={reportNameEdit}
                    requiredARIALabel={commonARIALabel.required}
                  />
                ) : (
                  <FormController
                    control={control}
                    name={PowerBiReportsDetailKey.reports}
                    ref={reportsRef}
                  >
                    <MuiMultiSelect
                      label={powerBiSiteCoreLabel.reports}
                      options={availableReports}
                      showSearch={availableReports.length > 0}
                      showChip
                      isMandatory
                      id={`${pageId}-${PowerBiReportsDetailKey.reports}`}
                      chipId={`${pageId}-${PowerBiReportsDetailKey.reports}-chip`}
                      isDialogTitleWithEllipsis
                      dialogTitle={powerBiSiteCoreLabel.reports}
                      selectPlaceHolder={commonLabels.selectPlaceHolder}
                      searchPlaceHolder={commonLabels.search}
                      selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                      doneText={commonLabels.done}
                      emptyMessage={getPowerBiReportsDetailLabel(
                        createUserSiteCoreKeys.noReportsAvailable,
                      )}
                      allARIAProps={multiSelectARIALabels}
                    />
                  </FormController>
                )}
              </MuiGrid>
            </MuiGrid>

            {/* Add/Update/Cancel reports buttons */}
            <MuiGrid container className="add-reports">
              {editingIndex === null ? (
                <MuiButtonSecondary onClick={handleAddReports}>
                  {commonLabels.add}
                </MuiButtonSecondary>
              ) : (
                <MuiBox className="edit-buttons">
                  <MuiButtonSecondary onClick={onCancelPowerBiReportsForm}>
                    {commonLabels.cancel}
                  </MuiButtonSecondary>
                  <MuiButtonPrimary onClick={handleAddReports}>
                    {commonLabels.update}
                  </MuiButtonPrimary>
                </MuiBox>
              )}
            </MuiGrid>

            {/* Reports Table */}
            <MuiBox className="reports-table">
              <MuiTable
                pageId={pageId}
                tableHeaderForm={[]}
                columns={columns}
                tableResults={
                  addedReports as unknown as Record<string, ReactNode>[]
                }
                noDataInApi={addedReports.length === 0}
                noResultMessage={commonLabels.noResult}
                emptyMessage={getPowerBiReportsDetailLabel(
                  createUserSiteCoreKeys.noReportsFoundMessage,
                )}
                priorityResults={[]}
                isFooterVisible={true}
                selectedId={
                  editingIndex !== null
                    ? addedReports[editingIndex]?.reportId
                    : ""
                }
                selectedIdKey="reportId"
              />
            </MuiBox>

            {/* Comments */}
            <MuiGrid container columnGap={3} className="comments-section">
              <MuiGrid size={{ md: 8, sm: 8, xs: 12 }}>
                <FormController
                  control={control}
                  name={PowerBiReportsDetailKey.comments}
                >
                  <MuiTextField
                    label={getPowerBiReportsDetailLabel(
                      createUserSiteCoreKeys.comment,
                    )}
                    placeholder={commonLabels.inputPlaceHolder}
                    isTextarea
                    multiline
                    rows={4}
                    maxLength={MAX_LENGTH_1000}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>
          </MuiBox>
        </AccordionHeader>
      </MuiBox>
    </CreateUserPowerBiReportsDetailWrapper>
  );
};
